/**
 * Everything a widget can draw, computed once and written to a shared file.
 *
 * ## The constraint that shapes all of this
 *
 * A widget has no network. On iOS it renders inside a WidgetKit extension with
 * its own tiny memory budget and no session; on Android it is a RemoteViews
 * tree assembled by the launcher. Neither can call AEGIS. So the app writes a
 * snapshot into a container both processes can see — an App Group on iOS,
 * SharedPreferences on Android — and the widget renders whatever is there.
 *
 * Three consequences run through every decision below:
 *
 * 1. **The snapshot holds every concept at once.** Changing the concept in the
 *    OS editor reloads the timeline without waking the app, so a snapshot that
 *    only held the selected one would go blank on every change.
 * 2. **`capturedAt` is not optional and not decoration.** It is what lets the
 *    widget say how old it is, and CLAUDE.md's rule that staleness travels
 *    with the data rather than in a separate flag applies with more force
 *    here than anywhere: nothing on a lock screen looks stale.
 * 3. **Money stays a string.** The widget parses for display only. A JSON
 *    number here would put a float in the one place a trader glances at
 *    without checking.
 *
 * ## It is built here, not in the app
 *
 * Pure over its inputs, so the shape the widget depends on is covered by
 * ordinary tests rather than by opening a phone. The native side then contains
 * only rendering — which is the part nobody can unit-test anyway, and the part
 * worth keeping as thin as possible.
 */
import { Decimal, ZERO, dec } from '../money.js';
import { widgetConcept, type WidgetConcept } from './concepts.js';

/** Widest a snapshot may be, in accounts. Beyond this the widget is a list. */
export const MAX_SNAPSHOT_ACCOUNTS = 8;

export interface SnapshotAccount {
  readonly accountId: string;
  readonly nickname: string;
  readonly currency: string;
  /** 'ok' | 'caution' | 'warning' | 'breached', from the rule engine. */
  readonly status: string;
  readonly equity: string;
  readonly realizedDayPnl: string;
  readonly unrealizedPnl: string;
  /**
   * Dollars before the nearest rule ends the account, or null when no rule set
   * applies. Null and zero are different facts and the widget says so.
   */
  readonly buffer: string | null;
  /** Which rule the buffer is measured against — "Trailing drawdown". */
  readonly bufferLabel: string | null;
  /** 0..1, for the ring. Null when there is no limit to be a fraction of. */
  readonly bufferUsed: number | null;
  /** The largest loss the account is permitted, for the ring's denominator. */
  readonly bufferLimit: string | null;
  readonly highWaterMark: string;
  /** True when the trailing floor has stopped moving. Unambiguously good news. */
  readonly drawdownFrozen: boolean;
  /**
   * How far the nearest market must move before the nearest rule breaks.
   *
   * Points rather than dollars because that is the unit a trader converts
   * without arithmetic. Null when nothing is open — a rule cannot be breached
   * by a market you are not in.
   */
  readonly pointsToBreach: string | null;
  readonly breachSymbol: string | null;
  readonly openCount: number;
  /** Symbols on, for the "you hold ES and NQ" line. Capped for a small screen. */
  readonly symbols: readonly string[];
}

export interface SnapshotRead {
  readonly symbol: string;
  readonly timeframe: string;
  /** Regime with underscores already replaced — the widget must not do prose. */
  readonly regime: string;
  readonly summary: string;
  /**
   * The sentence naming what would falsify the read.
   *
   * A read without a falsifier is a horoscope, so this is the load-bearing
   * half of the concept. It is a full sentence and only fits the wider
   * families; `invalidationPrice` is what the small ones draw.
   */
  readonly invalidation: string | null;
  /**
   * The bare level, for a widget with no room for a sentence.
   *
   * Cross-checked against the annotations rather than parsed out of the prose
   * and trusted — see `levelInSentence`. Null when no annotation corroborates
   * it, because a number invented from a regex is worse on a lock screen than
   * no number at all.
   */
  readonly invalidationPrice: string | null;
  readonly confidence: number;
  /** Epoch seconds. Distinct from the snapshot's own capture time. */
  readonly computedAt: number;
}

export interface SnapshotRelease {
  readonly title: string;
  readonly sourceId: string;
  /** Epoch seconds of the release. The widget counts down from THIS. */
  readonly at: number;
  readonly importance: string;
  /** The trader's own symbols this release touches, already intersected. */
  readonly affects: readonly string[];
}

export interface WidgetSnapshot {
  /** Bumped when the shape changes so an old native build can refuse it. */
  readonly version: number;
  /** Epoch seconds. What the widget renders its staleness from. */
  readonly capturedAt: number;
  /** Which account the widget shows when its configuration names none. */
  readonly defaultAccountId: string | null;
  readonly accounts: readonly SnapshotAccount[];
  /** The read for a widget whose configuration names no symbol. */
  readonly read: SnapshotRead | null;
  /**
   * Reads by symbol, for a widget that names one.
   *
   * Optional for the same reason `readSymbols` is: `SnapshotStore.read()`
   * refuses a snapshot whose version exceeds what the build supports, so a
   * SNAPSHOT_VERSION bump would blank the widget on every installed build until
   * each shipped a new binary. An added key is ignored by a build that does not
   * decode it.
   */
  readonly reads?: Readonly<Record<string, SnapshotRead>>;
  readonly release: SnapshotRelease | null;
  /**
   * The symbols a read could be shown for, held first then watchlist.
   *
   * Two jobs, which is why it is a list rather than a count. It is what a
   * per-widget symbol picker offers, and it is how the empty state tells
   * "AEGIS has not read this yet" apart from "you have not named a symbol
   * anywhere" — different problems, different actions.
   *
   * Optional, and deliberately so: `SnapshotStore.read()` refuses a snapshot
   * whose version exceeds what the build supports, so bumping SNAPSHOT_VERSION
   * would blank the widget on every installed build until each one shipped a
   * new binary. An added optional key is ignored by a build that does not
   * decode it and read by one that does. Nothing goes dark.
   *
   * Capped at `MAX_WIDGET_READ_SYMBOLS`. The file is written on every
   * foreground and an unbounded field in it is fine right up until one trader
   * watches four hundred symbols.
   */
  readonly readSymbols?: readonly string[];
}

/**
 * How many symbols a snapshot carries reads for.
 *
 * A read face is roughly 300 bytes of short strings, so twenty is about 6 KB —
 * next to nothing beside the eight-accounts-by-five-concepts ceiling already
 * accepted, and more than any realistic futures watchlist.
 */
export const MAX_WIDGET_READ_SYMBOLS = 20;

/** The shape version. A native build that reads a newer one renders nothing. */
export const SNAPSHOT_VERSION = 1;

// ---------------------------------------------------------------------------
// Inputs — deliberately structural rather than importing the API DTOs
// ---------------------------------------------------------------------------

/*
  These mirror `RuleState` and friends without importing them, because
  `packages/core` must not depend on the wire types: the wire types are the
  API's contract with its clients, and the snapshot is the app's contract with
  a widget. Coupling them would mean a field renamed for a screen silently
  changing what a widget reads.
*/
export interface RuleStateLike {
  readonly accountId: string;
  readonly equity: string;
  readonly realizedDayPnl: string;
  readonly unrealizedPnl: string;
  readonly highWaterMark: string;
  readonly drawdownFrozen: boolean;
  readonly overall: string;
  readonly checks: ReadonlyArray<{
    readonly id: string; readonly label: string; readonly status: string;
    readonly current: string; readonly limit: string | null;
    readonly headroom: string | null; readonly utilisation: number;
    /** What the figures are denominated in. See `RuleCheck.unit`. */
    readonly unit: 'money' | 'count' | 'ratio';
    /** True when breaching this rule ends the account. */
    readonly endsAccount: boolean;
  }>;
  readonly projected: ReadonlyArray<{
    readonly ruleId: string; readonly symbol: string;
    readonly pointsUntilBreach: string;
  }>;
  readonly positions: ReadonlyArray<{ readonly symbol: string }>;
}

export interface AccountLike {
  readonly id: string;
  readonly nickname: string;
  readonly currency: string;
}

export interface ReadLike {
  readonly symbol: string;
  readonly timeframe: string;
  readonly regime: string;
  readonly summary: string;
  readonly invalidation: string | null;
  readonly confidence: number;
  readonly computedAt: number;
  /**
   * Prices of the levels the analysis drew, for corroborating the sentence.
   *
   * The invalidation arrives as prose because that is what a chart panel
   * needs. A widget needs the number, and these are what let it be recovered
   * without trusting a regex on its own.
   */
  readonly levels: readonly number[];
  /** Decimals the instrument prints, so the level is not rendered as 6045.5. */
  readonly precision: number;
}

export interface ReleaseLike {
  readonly sourceId: string;
  readonly title: string;
  /** ISO instant, as the calendar reports it. */
  readonly at: string;
  readonly importance: string;
  readonly affects: readonly string[];
  readonly enabled: boolean;
}

export interface SnapshotInput {
  readonly accounts: readonly AccountLike[];
  /** Keyed by account id. An account with no entry is reported without a buffer. */
  readonly rules: ReadonlyMap<string, RuleStateLike>;
  /** The read a widget shows when its configuration names no symbol. */
  readonly read: ReadLike | null;
  /**
   * Every read a widget COULD be pointed at, in any order.
   *
   * Filtered against `readSymbols` rather than trusted: the picker offers that
   * list, so a read for a symbol not on it is one no widget can be set to, and
   * carrying it would put the cap somewhere other than where it is declared.
   */
  readonly reads?: readonly ReadLike[];
  readonly upcoming: readonly ReleaseLike[];
  /** Now, as epoch seconds. Injected so the builder stays pure. */
  readonly now: number;
  /** The account the widget defaults to. Falls back to the first. */
  readonly preferredAccountId?: string | null;
  /**
   * Symbols a read could be shown for, in the order they should be offered.
   *
   * Held first, then watchlist — the caller has already decided that order and
   * this does not re-sort it, only cap it. Absent falls back to the symbols the
   * accounts hold, which the builder knows anyway, so a caller that has not
   * been updated still reports a trader's own positions as readable.
   */
  readonly readSymbols?: readonly string[];
}

/** At most this many symbols on a line before it stops fitting a lock screen. */
const MAX_SYMBOLS = 4;

/**
 * The nearest rule to breaking, and what it is measured against.
 *
 * Two filters and a comparison, and every one of them was a bug first.
 *
 * 1. **Money only.** Three of the rules are counts and one is a ratio. Ranking
 *    across all of them compared $1,800 of drawdown against 0.26 of a
 *    consistency ratio and against 5 trading days, and the smallest number
 *    always won — so a trader with real room was going to be shown
 *    "Buffer $0.00 — Minimum trading days" on a lock screen.
 * 2. **Rules that END the account.** A profit target is money too, and
 *    breaching it is the goal. A buffer that could name it would eventually
 *    report a trader's progress as their peril.
 * 3. **By headroom, not by utilisation.** A rule at 90% of a $500 limit has
 *    $50 left; one at 60% of a $3,000 limit has $1,200. The first ends the
 *    account and ranking by percentage names the second.
 */
function nearestRule(state: RuleStateLike): RuleStateLike['checks'][number] | null {
  let best: RuleStateLike['checks'][number] | null = null;
  let bestHeadroom: Decimal | null = null;

  for (const c of state.checks) {
    if (c.headroom === null) continue;
    if (c.unit !== 'money' || !c.endsAccount) continue;
    const h = dec(c.headroom);
    // A breached rule has no headroom left to compare, and it is the answer.
    if (c.status === 'breached') return c;
    if (bestHeadroom === null || h.lt(bestHeadroom)) {
      best = c;
      bestHeadroom = h;
    }
  }
  return best;
}

/**
 * How far the nearest market must move, in points.
 *
 * The smallest distance across every projection, because the account breaks on
 * whichever comes first — not on the one that happens to be listed first.
 */
function nearestBreach(
  state: RuleStateLike,
): { points: string; symbol: string } | null {
  let best: { points: Decimal; symbol: string } | null = null;
  for (const p of state.projected) {
    const pts = dec(p.pointsUntilBreach);
    if (best === null || pts.lt(best.points)) best = { points: pts, symbol: p.symbol };
  }
  return best === null ? null : { points: best.points.toFixed(2), symbol: best.symbol };
}

function accountFrom(
  account: AccountLike, state: RuleStateLike | undefined,
): SnapshotAccount {
  if (!state) {
    /*
      An account with no rule state is reported rather than dropped.

      It is a real state — a personal live account with no prop rule set, or an
      account whose evaluation failed to load — and a widget that silently
      omitted it would show a trader a smaller book than they hold.
    */
    return {
      accountId: account.id, nickname: account.nickname, currency: account.currency,
      status: 'ok',
      equity: '0.00', realizedDayPnl: '0.00', unrealizedPnl: '0.00',
      buffer: null, bufferLabel: null, bufferUsed: null, bufferLimit: null,
      highWaterMark: '0.00', drawdownFrozen: false,
      pointsToBreach: null, breachSymbol: null,
      openCount: 0, symbols: [],
    };
  }

  const rule = nearestRule(state);
  const breach = nearestBreach(state);
  const symbols = [...new Set(state.positions.map((p) => p.symbol))];

  return {
    accountId: account.id,
    nickname: account.nickname,
    currency: account.currency,
    status: state.overall,
    equity: state.equity,
    realizedDayPnl: state.realizedDayPnl,
    unrealizedPnl: state.unrealizedPnl,
    buffer: rule?.headroom ?? null,
    bufferLabel: rule?.label ?? null,
    // Clamped, because a breached rule reports utilisation above 1 and a ring
    // drawn past full reads as an error in the widget rather than in the book.
    bufferUsed: rule ? Math.max(0, Math.min(1, rule.utilisation)) : null,
    bufferLimit: rule?.limit ?? null,
    highWaterMark: state.highWaterMark,
    drawdownFrozen: state.drawdownFrozen,
    pointsToBreach: breach?.points ?? null,
    breachSymbol: breach?.symbol ?? null,
    openCount: state.positions.length,
    symbols: symbols.slice(0, MAX_SYMBOLS),
  };
}

/**
 * The next release worth counting down to.
 *
 * Filtered to what the trader is actually exposed to, then to what has not
 * already happened, then to sources that are switched on. A countdown to a
 * release nobody is watching is the empty widget the concept was warned about.
 */
function nextRelease(
  upcoming: readonly ReleaseLike[], held: ReadonlySet<string>, now: number,
): SnapshotRelease | null {
  let best: SnapshotRelease | null = null;

  for (const r of upcoming) {
    if (!r.enabled) continue;
    const at = Math.floor(new Date(r.at).getTime() / 1000);
    if (!Number.isFinite(at) || at <= now) continue;

    const affects = r.affects.filter((s) => held.has(s));
    // Nothing held that this touches, and something IS held: not this trader's
    // release. With nothing held at all the calendar is still worth showing,
    // because "flat before CPI" is a decision too.
    if (held.size > 0 && affects.length === 0) continue;

    if (best === null || at < best.at) {
      best = {
        title: r.title, sourceId: r.sourceId, at,
        importance: r.importance,
        affects: affects.length > 0 ? affects : r.affects.slice(0, MAX_SYMBOLS),
      };
    }
  }
  return best;
}

/** Build the snapshot the widget renders from. Pure over its inputs. */
export function buildSnapshot(input: SnapshotInput): WidgetSnapshot {
  const accounts = input.accounts
    .slice(0, MAX_SNAPSHOT_ACCOUNTS)
    .map((a) => accountFrom(a, input.rules.get(a.id)));

  const held = new Set(accounts.flatMap((a) => a.symbols));

  /*
    The default account is the one nearest to breaking, not the first.

    A trader with a funded account and an evaluation cares about whichever is
    closest to ending, and "first" is an ordering the broker chose. An explicit
    preference still wins — this is only the fallback.
  */
  const preferred = input.preferredAccountId
    && accounts.some((a) => a.accountId === input.preferredAccountId)
    ? input.preferredAccountId
    : nearestToBreaking(accounts);

  /*
    Falls back to what the trader HOLDS, not to nothing.

    The caller supplies held-then-watchlist because only it knows the
    watchlist. But the held half is already here, and defaulting to empty would
    tell a trader with an open ES position that they have not named a symbol
    anywhere — which is false, and points them at the wrong action.

    Deduplicated, because a symbol held in two accounts is one symbol to a
    picker, and capped after that so the cap counts what the trader would
    actually see rather than counting the duplicates away.
  */
  const readSymbols = [...new Set(input.readSymbols ?? [...held])]
    .slice(0, MAX_WIDGET_READ_SYMBOLS);

  return {
    version: SNAPSHOT_VERSION,
    capturedAt: input.now,
    defaultAccountId: preferred,
    accounts,
    readSymbols,
    read: input.read === null ? null : readFrom(input.read),
    /*
      Every read the picker could be pointed at, keyed by symbol.

      A widget set to a symbol reloads its timeline WITHOUT waking the app, so
      the file has to already hold the answer for every symbol that widget could
      have been set to. Holding only the one the server picked would leave a
      widget showing an empty tile until AEGIS was next opened — the same
      reasoning that puts all five concepts in here rather than the selected one.

      Capped by the caller's `readSymbols`, so this cannot grow past
      MAX_WIDGET_READ_SYMBOLS however many reads it is handed.
    */
    reads: Object.fromEntries(
      (input.reads ?? [])
        .filter((r) => readSymbols.includes(r.symbol))
        .map((r) => [r.symbol, readFrom(r)]),
    ),
    release: nextRelease(input.upcoming, held, input.now),
  };
}

/** One analysis, projected into what a widget draws. */
function readFrom(r: ReadLike): SnapshotRead {
  return {
    symbol: r.symbol,
    timeframe: r.timeframe,
    // The widget draws text; it does not transform it. Prose decisions live
    // on this side of the boundary where they can be tested.
    regime: r.regime.replace(/[_-]/g, ' '),
    summary: r.summary,
    invalidation: r.invalidation,
    invalidationPrice: levelInSentence(r.invalidation, r.levels, r.precision),
    confidence: r.confidence,
    computedAt: r.computedAt,
  };
}

/**
 * The level a sentence is about, recovered without trusting the sentence.
 *
 * The invalidation is prose — "a sustained move through 6045.5 (session high)
 * would remove the basis for this reading" — because that is what a chart
 * panel needs. A 62pt circular widget needs `6045.50` and nothing else.
 *
 * Numbers are pulled out of the sentence and then CORROBORATED against the
 * levels the analysis actually drew. A regex alone would happily return the
 * `5` from "5m" or a year from a headline; requiring the number to also be a
 * level the model plotted makes a wrong answer take two independent failures
 * rather than one.
 *
 * Ambiguity resolves to null. Two corroborated numbers in one sentence means
 * the prose changed shape, and guessing which is the invalidation is exactly
 * the kind of confident wrong answer a lock screen must not carry.
 */
export function levelInSentence(
  sentence: string | null, levels: readonly number[], precision: number,
): string | null {
  if (!sentence || levels.length === 0) return null;

  // Thousands separators are stripped so "6,045.5" and "6045.5" compare equal.
  const found = [...sentence.matchAll(/\d[\d,]*(?:\.\d+)?/g)]
    .map((m) => Number(m[0]!.replace(/,/g, '')))
    .filter((n) => Number.isFinite(n));

  const matched = [...new Set(
    found.filter((n) => levels.some((l) => Math.abs(l - n) < 1e-6)),
  )];

  if (matched.length !== 1) return null;
  return matched[0]!.toFixed(Math.max(0, Math.min(8, precision)));
}

/** Whichever account has the least room left, or null when none has a buffer. */
function nearestToBreaking(accounts: readonly SnapshotAccount[]): string | null {
  if (accounts.length === 0) return null;

  let best: SnapshotAccount | null = null;
  let bestBuffer: Decimal | null = null;
  for (const a of accounts) {
    if (a.buffer === null) continue;
    const b = dec(a.buffer);
    if (bestBuffer === null || b.lt(bestBuffer)) { best = a; bestBuffer = b; }
  }
  // No account has a rule set: the first is as good an answer as any, and a
  // null here would leave the widget with nothing to show a trader who has an
  // account.
  return (best ?? accounts[0]!).accountId;
}

/**
 * Whether a concept has anything to draw from this snapshot.
 *
 * Used by the picker to warn before the trader chooses, and by the renderer to
 * pick an empty state that says WHY rather than showing a blank tile. The
 * distinction matters: "no open position" and "AEGIS has not read this symbol
 * yet" are different facts and a trader can act on the first.
 */
export function conceptHasData(
  snapshot: WidgetSnapshot,
  concept: WidgetConcept,
  accountId?: string | null,
  /** Which symbol's read is being asked about. Absent means the default. */
  symbol?: string | null,
): boolean {
  const account = accountId
    ? snapshot.accounts.find((a) => a.accountId === accountId)
    : snapshot.accounts.find((a) => a.accountId === snapshot.defaultAccountId);

  switch (concept) {
    case 'buffer':
    case 'gauge':
      return account?.buffer !== null && account?.buffer !== undefined;
    case 'session':
      // The day's realised P&L exists whether or not anything is on, which is
      // the whole reason this concept degrades gracefully for a flat trader.
      return account !== undefined;
    case 'read':
      return readFor(snapshot, symbol) !== null;
    case 'release':
      return snapshot.release !== null;
    default:
      return false;
  }
}

/**
 * The read a widget is asking for.
 *
 * A named symbol wins; anything else falls back to the read the server picked.
 * "Anything else" covers two states that must behave the same and are reached
 * differently: a widget placed before the picker existed carries no symbol at
 * all, and a widget set to a symbol the trader has since dropped from their
 * watchlist carries one the snapshot no longer has. A configuration outlives
 * the list that produced it, so neither can be an error.
 */
export function readFor(
  snapshot: WidgetSnapshot, symbol?: string | null,
): SnapshotRead | null {
  if (!symbol) return snapshot.read;

  const named = snapshot.reads?.[symbol];
  if (named) return named;

  /*
    Offered but unread is NOT the same as no longer offered, and treating them
    alike was wrong in the direction that matters.

    A symbol still on the trader's list that AEGIS has not analysed yet has a
    real answer — "not read yet", which opening the chart resolves. Falling back
    there hands them a DIFFERENT symbol's regime instead, because they picked
    CL and the snapshot had ES. The tile is not lying — it says ES — but they
    asked for CL and got something else without being told why.

    A symbol no longer on the list is the other case: the widget's configuration
    outlived the watchlist that produced it, and the default read is the right
    answer because there is no answer for a symbol nobody is tracking.
  */
  if ((snapshot.readSymbols ?? []).includes(symbol)) return null;
  return snapshot.read;
}

/**
 * How old the snapshot is, in seconds.
 *
 * Computed here rather than in two renderers, so a phone and a launcher cannot
 * disagree about whether a figure is stale.
 */
export function snapshotAgeSec(snapshot: WidgetSnapshot, now: number): number {
  return Math.max(0, now - snapshot.capturedAt);
}

/**
 * Beyond this a figure is old enough that the widget says so out loud.
 *
 * Fifteen minutes. Chosen against the refresh budget rather than against
 * taste: iOS grants roughly 40–70 timeline reloads a day and decides the
 * spacing itself, so a widget that only marked itself stale after an hour
 * would be silently wrong through most of a session.
 */
export const STALE_AFTER_SEC = 15 * 60;

export function isStale(snapshot: WidgetSnapshot, now: number): boolean {
  return snapshotAgeSec(snapshot, now) > STALE_AFTER_SEC;
}

/**
 * A concept's staleness tolerance, as a sentence the renderer can show.
 *
 * Null when there is nothing to say. Non-null is not a warning about the app —
 * it is a statement about the figure, and the trader is the one who decides
 * whether it matters.
 */
export function stalenessNote(
  concept: WidgetConcept, ageSec: number,
): string | null {
  if (ageSec <= STALE_AFTER_SEC) return null;
  const mins = Math.round(ageSec / 60);
  const spec = widgetConcept(concept);
  return spec.honestWhenStale
    ? `${mins} min old`
    : `${mins} min old — this figure moves with price`;
}

/**
 * The one line a tight family draws when it has room for exactly one.
 *
 * ## The defect this exists to make impossible
 *
 * `RectangularFace` on iOS drew `staleness`, else `footnote`, else `extra` —
 * an `if / else if` chain over three optional fields. That reads as a sensible
 * priority order and is a compliance defect: `footnote` carries
 * `AEGIS read · not advice`, the attribution CLAUDE.md non-negotiable 4 says
 * the renderer may not drop, and `staleness` becomes non-null on the *same*
 * face the moment a read passes fifteen minutes old.
 *
 * So every read older than a quarter of an hour — which on a lock screen is
 * most of them — rendered a machine opinion with no attribution. Verified by
 * running the arithmetic rather than by reading it: at 16, 30 and 120 minutes
 * the chain selects `staleness` and the attribution is never drawn.
 *
 * Fixing it in the Swift by drawing both lines would have been a layout change
 * on a surface whose height the OS fixes and which nobody here can render. So
 * the two are composed into ONE line instead: the family draws the same number
 * of lines it drew before, and the attribution is inside the one it draws.
 *
 * ## Why it lives in core
 *
 * Three renderers show this line — iOS, Android and the web preview. A copy
 * per surface is a copy that drifts, and this particular sentence is the one
 * whose drift is a regulatory question rather than a cosmetic one. Same
 * reasoning that moved the spine's stamps here.
 *
 * Order is deliberate: the caveat leads, because a trader who reads only the
 * first few words should get "this is old" before "this is a machine read".
 * Both are present either way.
 */
export function faceCaption(face: {
  readonly staleness: string | null;
  readonly footnote: string | null;
  readonly extra: string | null;
}): string | null {
  const { staleness, footnote, extra } = face;

  // The attribution is not droppable, so it is never traded against anything.
  // When both exist they share a line rather than competing for one.
  if (staleness !== null && footnote !== null) return `${staleness} · ${footnote}`;
  if (footnote !== null) return footnote;
  if (staleness !== null) return staleness;

  // `extra` is the only one of the three that IS optional — a nicety, shown
  // when nothing more important needs the line.
  return extra;
}

/** Zero, as the snapshot writes it. Exported so tests and the API agree. */
export const SNAPSHOT_ZERO = ZERO.toFixed(2);
